import type { FilterSelectionMode } from '@api/api/screener';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import styles from '@terminal/components/filter/Filter.module.scss';
import type { IncludeExcludeLabels } from '@terminal/components/filter/include-exclude/include-exclude.types';
import {
  getFilterOptionGroupBySelectionMode,
  INCLUDE_EXCLUDE_SELECTION_MODES,
} from '@terminal/components/filter/include-exclude/utils/include-exclude.utils';
import { Option } from '@terminal/components/filter/Option';
import type { ActiveFilterOptions } from '@terminal/components/filters/Filters';
import type { FilterOption } from '@terminal/components/update-filter-options/updateFilterOptions';
import type {
  FilterGroupKeys,
  UseFilterActionsReturn,
} from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useState } from 'react';

interface DynamicIncludeExcludeOptionsProps<T extends FilterGroupKeys> {
  addFilterOption: UseFilterActionsReturn<T>['addFilterOption'];
  disabledOptions?: FilterOption[];
  filterId: string;
  filterKey: string;
  filterOptionGroups: ActiveFilterOptions[];
  initialSelectionMode: FilterSelectionMode;
  labels: IncludeExcludeLabels;
  noResultsLabel: string;
  options: FilterOption[];
  showEmpty: boolean;
}

export const DynamicIncludeExcludeOptions = <T extends FilterGroupKeys>({
  addFilterOption,
  disabledOptions,
  filterId,
  filterKey,
  filterOptionGroups,
  initialSelectionMode,
  labels,
  noResultsLabel,
  options,
  showEmpty,
}: DynamicIncludeExcludeOptionsProps<T>) => {
  const [selectionMode, setSelectionMode] = useState(initialSelectionMode);
  const selectedOptionKeys = new Set(
    getFilterOptionGroupBySelectionMode(
      filterOptionGroups,
      selectionMode
    )?.options.map(option => option.key)
  );
  const disabledOptionKeys = new Set(
    disabledOptions?.map(option => option.key)
  );

  const handleOptionSelect = (option: FilterOption) => {
    addFilterOption({
      dynamic: true,
      filterId,
      filterKey,
      multiselect: false,
      option,
      selectionMode,
    });
  };

  return (
    <>
      <div className={styles.includeExcludeFilterMode}>
        <ButtonGroup
          labels={[labels.include(), labels.exclude()]}
          activeIndex={INCLUDE_EXCLUDE_SELECTION_MODES.indexOf(selectionMode)}
          onClick={index =>
            setSelectionMode(
              INCLUDE_EXCLUDE_SELECTION_MODES[index] ?? 'include'
            )
          }
        />
      </div>
      <div className={styles.separator} />
      <div className={styles.scrollableList}>
        {showEmpty ? (
          <p className={styles.noResults}>{noResultsLabel}</p>
        ) : (
          options.map(option => {
            const disabled = disabledOptionKeys.has(option.key);

            return (
              <Option
                key={option.key}
                option={option}
                disabled={disabled}
                isChecked={selectedOptionKeys.has(option.key) && !disabled}
                handleClick={() => handleOptionSelect(option)}
              />
            );
          })
        )}
      </div>
    </>
  );
};
